/**
 * BillingAccountBYOKPage - Gestion des clés API partagées (Phase 3 Agency)
 * 
 * Permet aux utilisateurs Agency de configurer des clés API au niveau BillingAccount.
 * Ces clés sont héritées par tous les workspaces enfants qui n'ont pas leurs propres clés.
 */
import React, { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { Eye, EyeOff, Save, AlertCircle, CheckCircle, Key } from 'lucide-react'

interface BillingAccountBYOK {
  openai_key_hint: string | null
  anthropic_key_hint: string | null
  ollama_key_hint: string | null
  provider: string | null
}

interface BillingAccount {
  id: number
  public_id: string
  owner_user_id: number
  created_at: string
}

export default function BillingAccountBYOKPage() {
  const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null)
  const [byokSettings, setByokSettings] = useState<BillingAccountBYOK | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // Form state
  const [openaiKey, setOpenaiKey] = useState('')
  const [anthropicKey, setAnthropicKey] = useState('')
  const [ollamaKey, setOllamaKey] = useState('')
  
  // Visibility toggles
  const [showOpenai, setShowOpenai] = useState(false)
  const [showAnthropic, setShowAnthropic] = useState(false)
  const [showOllama, setShowOllama] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Récupérer le BillingAccount de l'utilisateur
      const { data: ba } = await api.get('/billing-accounts/me/billing-account')
      setBillingAccount(ba)
      
      // Récupérer les settings BYOK
      const { data: byok } = await api.get(`/billing-accounts/${ba.id}/settings/byok`)
      setByokSettings(byok)
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError('Aucun BillingAccount trouvé. Cette fonctionnalité est réservée aux plans Agency.')
      } else {
        setError(err?.response?.data?.detail || 'Erreur lors du chargement')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!billingAccount) return
    
    setSaving(true)
    setError(null)
    setSuccess(false)
    
    try {
      const payload: any = {}
      
      // Envoyer uniquement les clés modifiées
      if (openaiKey.trim()) {
        payload.openai_api_key = openaiKey
      }
      if (anthropicKey.trim()) {
        payload.anthropic_api_key = anthropicKey
      }
      if (ollamaKey.trim()) {
        payload.ollama_api_key = ollamaKey
      }
      
      const { data } = await api.patch(
        `/billing-accounts/${billingAccount.id}/settings/byok`,
        payload
      )
      
      setByokSettings(data)
      setSuccess(true)
      
      // Réinitialiser les champs
      setOpenaiKey('')
      setAnthropicKey('')
      setOllamaKey('')
      setShowOpenai(false)
      setShowAnthropic(false)
      setShowOllama(false)
      
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">Chargement...</div>
      </div>
    )
  }

  if (error && !billingAccount) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-red-900 dark:text-red-100">
                Accès refusé
              </h2>
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
          <Key className="h-6 w-6 text-emerald-600" />
          Clés API Partagées (Agency)
        </h1>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          Configurez des clés API au niveau de votre compte Agency. Ces clés seront héritées par tous vos workspaces 
          qui n'ont pas leurs propres clés configurées.
        </p>
      </div>

      {/* Success message */}
      {success && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              Clés API mises à jour avec succès
            </p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && billingAccount && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* OpenAI */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">OpenAI</h3>
            {byokSettings?.openai_key_hint && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                Clé actuelle : ****{byokSettings.openai_key_hint}
              </p>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="relative">
            <input
              type={showOpenai ? 'text' : 'password'}
              placeholder="sk-..."
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              className="w-full px-3 py-2 pr-10 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => setShowOpenai(!showOpenai)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {showOpenai ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Laissez vide pour ne pas modifier. Cette clé sera héritée par tous les workspaces sans clé OpenAI.
          </p>
        </div>
      </div>

      {/* Anthropic */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Anthropic (Claude)</h3>
            {byokSettings?.anthropic_key_hint && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                Clé actuelle : ****{byokSettings.anthropic_key_hint}
              </p>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="relative">
            <input
              type={showAnthropic ? 'text' : 'password'}
              placeholder="sk-ant-..."
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              className="w-full px-3 py-2 pr-10 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => setShowAnthropic(!showAnthropic)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {showAnthropic ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Laissez vide pour ne pas modifier. Cette clé sera héritée par tous les workspaces sans clé Anthropic.
          </p>
        </div>
      </div>

      {/* Ollama */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Ollama Cloud</h3>
            {byokSettings?.ollama_key_hint && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                Clé actuelle : ****{byokSettings.ollama_key_hint}
              </p>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="relative">
            <input
              type={showOllama ? 'text' : 'password'}
              placeholder="ollama-..."
              value={ollamaKey}
              onChange={(e) => setOllamaKey(e.target.value)}
              className="w-full px-3 py-2 pr-10 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => setShowOllama(!showOllama)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {showOllama ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Laissez vide pour ne pas modifier. Cette clé sera héritée par tous les workspaces sans clé Ollama.
          </p>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || (!openaiKey && !anthropicKey && !ollamaKey)}
          className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer les clés'}
        </button>
      </div>

      {/* Info box */}
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
          💡 Comment fonctionne l'héritage ?
        </h4>
        <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
          <li>Si un workspace a sa propre clé configurée, elle sera utilisée en priorité</li>
          <li>Si un workspace n'a pas de clé, il héritera automatiquement de la clé du BillingAccount</li>
          <li>Cela permet de centraliser la gestion des clés API pour tous vos workspaces</li>
          <li>Les clés sont chiffrées avant d'être stockées en base de données</li>
        </ul>
      </div>
    </div>
  )
}
